import apiFetch from '@scale/frontend-lib/api-fetch';
import { LintDiagnostic, LintSeverity } from '@scale/shared/task_lint/task_linter';
import { LintResult, IProcessedLintResults, LintRule } from '@scale/shared/types/linter';
import { LintingRequestParams } from '@scale/shared/types/api/linting';
import { captureClientSideError } from '@scale/frontend-lib/errors';
import logger from '@scale/frontend-lib/logging';
import { TaskType } from '@scale/shared-types';
import { unleashFeatureFlagEnabled } from 'react-scale/lib/featureFlag';
import qs from 'qs';
import { AttemptBehaviourLog } from '@scale/shared/types/api/complete';
import { transformLintResultsToDiagnostics } from '@scale/shared/task_lint/lintResponseUtils';

type LintedSubtask = {
  type: TaskType;

  attemptId?: string;
} & (
  | {
      project: {
        _id: string;
        taskCustomer: string;
      };
      _id: string;
    }
  | {
      projectId: string;
      assignmentId: string;
    }
);

interface IFetchApiLintResultsOpts {
  subtask: LintedSubtask;
  response: $FIXME;
  // List of lint rules to run
  // If undefined or empty, run all lint rules
  lintRuleNames?: string[];
  isPerTurn?: boolean;
  attemptBehaviourLog?: AttemptBehaviourLog;
  zEditorSelectedAnnotations?: string[];
}

export const fetchApiLintResults = async ({
  subtask,
  response,
  lintRuleNames,
  isPerTurn,
  attemptBehaviourLog,
  zEditorSelectedAnnotations,
}: IFetchApiLintResultsOpts): Promise<LintDiagnostic[]> => {
  const projectId = 'project' in subtask ? subtask.project._id : subtask.projectId;
  const reqParams: LintingRequestParams = {
    taskType: subtask.type,
    projectId,
    taskCustomer: 'project' in subtask ? subtask.project.taskCustomer : undefined,
    data: response,
    lintRuleNames,
    isPerTurn,
    attemptBehaviourLog,
    zEditorSelectedAnnotations,
  };

  let lintOutput: IProcessedLintResults | undefined;

  const subtaskId = '_id' in subtask ? subtask._id : subtask.assignmentId;
  try {
    lintOutput = await apiFetch<IProcessedLintResults | undefined>(
      null,
      `internal/linting/subtasks/${subtaskId}/lint`,
      {
        body: reqParams,
        method: 'POST',
        redirectOnUnauthorized: true,
      },
    );
  } catch (err) {
    logger.error(`Failed to fetchApiLintResults for ${subtaskId}`, {
      error: JSON.stringify(err),
      errorMessage: err?.message,
      projectId,
      subtaskId,
      lintRuleNames,
      isPerTurn,
    });
    captureClientSideError(err, 'linter', { subtaskId });
    const shouldPass = await unleashFeatureFlagEnabled({
      feature: 'should-pass-on-lint-error',
      querystring: qs.stringify({ projectId }),
    });
    return shouldPass
      ? []
      : [
          new LintDiagnostic(`linting-error`, LintSeverity.ERROR, {
            message: 'Error running linters. Please try again.',
          }),
        ];
  }

  if (!lintOutput) return [];

  return transformLintResultsToDiagnostics(lintOutput.lintResults);
};

export const fetchLintResponseVerdict = async (
  params: any,
): Promise<{ lintResults: LintResult<any>[] }> => {
  try {
    const response = await apiFetch(null, `internal/linting/lint_response`, {
      body: params,
      method: 'POST',
      redirectOnUnauthorized: true,
    });
    return {
      lintResults: response.lintResults || [],
    };
  } catch (error) {
    captureClientSideError(error);
  }
  return { lintResults: [] };
};

export const fetchProcessedResponseLLM = async (params: any) => {
  try {
    const response = await apiFetch(null, `internal/linting/lint_grammar_spelling`, {
      body: params,
      method: 'POST',
      redirectOnUnauthorized: true,
    });
    return response.results || [];
  } catch (error) {
    captureClientSideError(error);
  }
  return [];
};

export const fetchApiLintRules = async (subtask: any) => {
  const reqParams = {
    taskType: subtask.type,
    projectId: 'project' in subtask ? subtask.project._id : subtask.projectId,
    taskCustomer: 'project' in subtask ? subtask.project.taskCustomer : undefined,
  };

  let lintRulesResponse: { lintRules: LintRule<any>[] } | undefined;
  try {
    const subtaskId = subtask._id ?? subtask.subtaskId ?? subtask.assignmentId;
    lintRulesResponse = await apiFetch<{ lintRules: LintRule<any>[] } | undefined>(
      null,
      `internal/linting/subtasks/${subtaskId}/rules`,
      {
        body: reqParams,
        method: 'POST',
        redirectOnUnauthorized: true,
      },
    );
  } catch (err) {
    captureClientSideError(err);
    throw err;
  }

  return lintRulesResponse?.lintRules ?? [];
};

export const checkAttemptMade = async (subtaskId: string) => {
  let result;
  try {
    result = await apiFetch(null, `internal/linting/check_attempt/${subtaskId}`, {
      method: 'POST',
      redirectOnUnauthorized: true,
    });
  } catch (err) {
    captureClientSideError(err);
    return false;
  }

  return result;
};
